import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AxiosRequestConfig } from "axios";
import { Spinner } from "components";
import { Option } from "components/Form/form";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setPurposeId } from "redux/features/CreateDeal";
import {
  resetState,
  setProductPurpose,
} from "redux/features/CreateOpportunity";
import { setIsReferralMade } from "redux/features/CustomerRegistration";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  requestQuotationL2ToL1,
  RequestQuotationL2ToL1RequestType,
} from "service/moneeflo/legacy-quotation-api";
import { postExistingOpportunity } from "service/apis/Opportunities";
import { toast } from "sonner";

interface Step2Props {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setPageTitle: React.Dispatch<React.SetStateAction<string>>;
  setProcessState: React.Dispatch<React.SetStateAction<any>>;
  handlePrevious: () => void;
  processState: any;
  connectedContactList?: any[];
  selectProduct?: string;
  productMode?: string;
  isProductLoan?: string;
}

const remitProductPurposeOptions: Option[] = [
  { value: "17", label: "Education Payment" },
  { value: "18", label: "Study Maintenance" },
  { value: "30", label: "GIC" },
  { value: "20", label: "Gift / Family Maintenance" },
];

interface PayloadDataType {
  contact_id: string;
  typeOfRefferal: string;
  refferedBy_staff: string;
  refferedBy_contacts: string;
  refferedBy_partner?: string;
  pipelineType: string;
  forex_branch: string;
  outlet_branch: string;
  exp_time: string;
  quotation_req: boolean;
  took_loan: boolean;
  mode: string;
  channel_of_interaction_id: string;
  items: Array<{
    subject: string;
    remarks: string;
    notes: string;
    assignedTo: string;
    product_id: string;
  }>;
}

export const L2Step7: React.FC<Step2Props> = ({
  setCurrentStep,
  setPageTitle,
  handlePrevious,
  processState,
  connectedContactList,
  setProcessState,
  selectProduct,
  productMode,
  isProductLoan,
}) => {
  const {
    product_purpose,
    remit_note,
    outlet_id,
    moneeflo_branch,
    medium_of_contact,
    quoteProduct,
    partner_reffered,
    employee_reffered,
    contact_reffered,
    prepare_quote_Contact_id,
  } = useAppSelector((state) => state.CreateOpportunity);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  const { getQuoteFlow }: any = location.state || {};

  // final lead create
  const handleSubmit = async ({ type = "" }) => {
    try {
      setLoading(true);
      const auth = localStorage.getItem("authToken");
      if (!auth) throw new Error("Authorization token not found");
      const authToken = JSON.parse(auth);

      const payloadData: PayloadDataType = {
        contact_id: prepare_quote_Contact_id as string,
        typeOfRefferal: "partner",
        refferedBy_staff: employee_reffered,
        refferedBy_contacts: contact_reffered,
        refferedBy_partner:
          medium_of_contact === "refferal" ? partner_reffered : "",
        pipelineType: "retail",
        forex_branch: moneeflo_branch,
        outlet_branch: outlet_id,
        exp_time: "1",
        quotation_req: false,
        took_loan: false,
        mode:
          processState?.Product === "Study Maintenance"
            ? "StudyMaintenance"
            : "Gift",
        channel_of_interaction_id: "12",
        items: [
          {
            subject: "",
            remarks: "",
            notes: remit_note,
            assignedTo: "",
            product_id: product_purpose,
          },
        ],
      };

      const payload: AxiosRequestConfig<PayloadDataType> = {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        data: payloadData,
      };

      const res = (await postExistingOpportunity(payload)) as any;
      if (res.status === 200) {
        setLoading(false);
        if (
          (res.data?.throwError as any)?.toLowerCase() ===
          "contact already exists"
        ) {
          toast.error("Contact already exists");
          return;
        }
        dispatch(setIsReferralMade(false));
        dispatch(setPurposeId(res?.data?.purposeID));
        dispatch(resetState());
        navigate("/l2createremittancequotation", {
          state: {
            contact_name: res?.data?.response?._contacts?.[0]?.contactName,
            product_id: res?.data?.response?.product_id,
            product_name: res?.data?.response?._product?.name,
            opportunity_id: res?.data?.response?.id,
            contact_id: res?.data?.response?.contacts_id,
            channel_of_interaction_id:
              res?.data?.response?.channel_of_interaction_id,
            note: res?.data?.response?.interactions_id?.[0]?.[0]?.note,
            remarks: "",
            ed_payment_mode: "",
            getQuoteFlow: getQuoteFlow,
            purpose_id: res?.data?.purposeID,
          },
        });
      }
    } catch (error) {
      setLoading(false);
      toast.error("Failed to create Lead");
    }
  };

  const productPurposeOptions = useMemo(
    () => ({
      remittance: remitProductPurposeOptions,
    }),
    [],
  );

  type ProductType = keyof typeof productPurposeOptions;

  const finalProductValue = useMemo(() => {
    return quoteProduct && quoteProduct in productPurposeOptions
      ? productPurposeOptions[quoteProduct as ProductType]?.filter(
          (item) => item.label === processState?.Product,
        )
      : [];
  }, [quoteProduct, processState, productPurposeOptions]);

  const handleCardSelect = useCallback(
    (item: any) => setSelectedCardId(item.id),
    [],
  );

  const handlePrepareQuoteDirection = useCallback(
    (item: any, event: React.FormEvent) => {
      event.preventDefault();

      if (quoteProduct === "remittance") {
        dispatch(setPurposeId(item?.purposeDetail?.purpose_id));
        navigate("/L2CreateRemittanceQuotation", {
          state: {
            contact_name: item?._contacts?.contactName,
            product_id: product_purpose,
            product_name: item?._product?.name,
            opportunity_id: item?.id,
            contact_id: item?.contacts_id,
            channel_of_interaction_id: item?._channel_of_interaction?.[0]?.id,
            note: "Prepare Quote",
            ed_payment_mode: productMode,
            loan: isProductLoan === "true" ? true : false,
            purpose_id: item?.purposeDetail?.purpose_id,
            getQuoteFlow: "getQuoteremittanceFlow",
          },
        });
      }
    },
    [
      navigate,
      quoteProduct,
      product_purpose,
      productMode,
      isProductLoan,
      location,
      finalProductValue,
      connectedContactList,
    ],
  );

  return (
    <div className="h-full flex flex-col overflow-hidden gap-5 py-3">
      {loading && <Spinner />}
      <div className="flex relative flex-col gap-3 items-start sm:pl-0 pl-10  justify-start w-[450px]">
        {quoteProduct && (
          <RadioGroup
            className="flex w-full flex-col gap-4"
            value={product_purpose}
            disabled={!!product_purpose}
          >
            {finalProductValue?.map((tab: { label: string; value: string }) => (
              <label
                key={tab.value}
                htmlFor={tab.value}
                className={`${
                  product_purpose === tab.value
                    ? " focus-within:border-blue-700 border-black-label"
                    : ""
                } flex w-auto items-center rounded-lg border p-3 space-x-2`}
              >
                <RadioGroupItem
                  value={tab.value}
                  className="w-4 h-4 text-blue-600 checked:ring-4 checked:ring-offset-4 checked:ring-offset-blue-700 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                />
                <p className="text-[14px] font-medium w-full">{tab.label}</p>
              </label>
            ))}
          </RadioGroup>
        )}
      </div>

      {/* This div will scroll and fill available space */}
      {connectedContactList && connectedContactList?.length > 0 && (
        <div className=" h-full overflow-auto">
          <div className="flex flex-1 overflow-auto flex-col p-4 sm:pl-0 pl-10 gap-8 w-full">
            <div>
              <label className="text-[14px] font-medium text-[#000]">
                We found an open lead for {processState?.Product}!
              </label>
            </div>
            {
              <div className=" max-h-[450px]">
                {connectedContactList?.map((item: any) => {
                  return (
                    <div
                      className={`flex flex-row w-[650px] justify-between p-5 mb-4 rounded-lg cursor-pointer ${
                        selectedCardId === item.id
                          ? "border-2 border-blue-500"
                          : "border-1 border-[#88AEF3]"
                      }`}
                      style={{
                        background:
                          "linear-gradient(89.99deg, #FFFFFF 0.01%, #E1EBFC 99.99%)",
                        justifyContent: "space-between",
                      }}
                      onClick={() => handleCardSelect(item)}
                      key={item?.id}
                    >
                      <div
                        className="flex flex-col gap-4"
                        style={{
                          justifyContent: "space-between",
                        }}
                      >
                        <h2 className="text-left">
                          <Label className="text-[18px] ">
                            {item?._product?.name}
                          </Label>
                          <span className="text-[#999999] px-2">|</span>
                          <Label className="text-[14px] text-[#999999] leading-[140%]">
                            {item?.mode_opp || "N/A"}
                          </Label>
                        </h2>
                        <div className="flex flex-row  items-center gap-2">
                          <Label className="text-[#999999] font-noraml text-[14px]">
                            Lead ID: OP - {item?.id} |
                          </Label>
                          <Label className="text-[#999999] text-[14px] font-noraml">
                            {new Date(item?.created_at)
                              .toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })
                              .replace(",", "  |")
                              .replace(/ /g, " - ")
                              .replace(" - |", " |")
                              .replace(/-\s/g, " ")
                              .toUpperCase()}
                          </Label>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                          <img src="/images/ProfileIConNew.svg" />
                          <Label className="text-[16px] font-normal">
                            {item?._contacts?.contactName || "N/A"}
                          </Label>
                        </div>
                      </div>
                      <div
                        className="flex flex-col"
                        style={{
                          justifyContent: "space-between",
                        }}
                      >
                        <Label
                          className="p-3 bg-[#E1FFE2] text-[#23AB28]"
                          style={{ borderRadius: "10px", textAlign: "center" }}
                        >
                          {" "}
                          {item?._contacts?.customer ? "Customer" : "Contact"}
                        </Label>

                        <div className="flex flex-row gap-8">
                          <button
                            type="button"
                            disabled={
                              !item?.interactions_id?.at(-1)?.quotation_pdf
                            }
                            className={`text-[14px] font-bold underline ${
                              item?.interactions_id?.at(-1)?.quotation_pdf
                                ? "text-[#165FE3] cursor-pointer"
                                : "text-[#A0C1E8] cursor-not-allowed"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const pdfUrl =
                                item?.interactions_id?.at(-1)?.quotation_pdf;
                              if (pdfUrl) {
                                window.open(pdfUrl, "_blank");
                              }
                            }}
                          >
                            View Quote
                          </button>
                          <button
                            className="text-[#165FE3] text-[14px] font-bold underline"
                            onClick={(e) =>
                              handlePrepareQuoteDirection(item, e)
                            }
                          >
                            Prepare Quote
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          </div>
        </div>
      )}

      {/* Footer buttons that stick to bottom */}
      <div className="flex gap-5 sm:pl-0 pl-10 h-fit">
        <button
          className="w-[100px] text-[14px] text-blue-500 p-3 bg-transparent border border-blue-500 rounded-lg flex items-center justify-center gap-2"
          onClick={() => {
            dispatch(setProductPurpose(""));
            setCurrentStep(2);
            setProcessState((prevState: any) => ({
              ...prevState,
              Product: "",
            }));
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 14.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L8.414 9l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back
        </button>
        <button
          className={`w-[150px] text-[14px] text-[#fff] p-3 ${
            !product_purpose ? "bg-blue-200" : "bg-[#165FE3]"
          } rounded-lg text-center`}
          disabled={!product_purpose}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit({ type: "" });
          }}
        >
          Get Quote
        </button>
      </div>
    </div>
  );
};
